// MentionPopover (DESIGN §3.12.7): Liste über dem Composer (max. 320 × 280), Einträge 40 mit
// Avatar 24 + Name `button` + Titel `label` `fg-secondary`; Gruppen „People“, „Channel“,
// „Assistant“. Gewählt `tile`. Tastatur führt der Composer (aria-activedescendant).
import type { ReactNode } from 'react';
import { floatingSurface, menuGroupLabel, menuItemBase } from './_lib/menuStyles';
import { cn } from '../basis/kern';

export type MentionGroup = 'people' | 'channel' | 'assistant';

export interface MentionItem {
  id: string;
  /** Eingefügter Name („Lena Fischer“, „channel“). */
  label: string;
  description?: string;
  /** Avatar 24 bzw. Bot-Kachel. */
  avatar?: ReactNode;
  group: MentionGroup;
}

const GROUP_TITLES: Record<MentionGroup, string> = {
  people: 'People',
  channel: 'Channel',
  assistant: 'Assistant',
};
const GROUP_ORDER: MentionGroup[] = ['people', 'channel', 'assistant'];

export const mentionOptionId = (listId: string, index: number) => `${listId}-opt-${index}`;

/** Reihenfolge der Anzeige (nach Gruppen); Index = Position in dieser Liste. */
export const orderMentions = (items: MentionItem[]) =>
  GROUP_ORDER.flatMap((group) => items.filter((item) => item.group === group));

export interface MentionPopoverProps {
  id: string;
  /** Bereits gefiltert und mit `orderMentions` sortiert. */
  items: MentionItem[];
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
  onSelect: (item: MentionItem) => void;
  loading?: boolean;
  className?: string;
}

export function MentionPopover({
  id,
  items,
  activeIndex,
  onActiveIndexChange,
  onSelect,
  loading,
  className,
}: MentionPopoverProps) {
  return (
    <div
      className={cn(
        floatingSurface,
        'absolute bottom-full left-0 mb-2 max-h-[280px] w-[min(320px,100%)] overflow-y-auto p-1',
        className,
      )}
    >
      <div id={id} role="listbox" aria-label="Mention suggestions">
        {loading && items.length === 0 ? (
          <div aria-hidden="true" className="space-y-1 p-1">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex h-10 items-center gap-3 px-2">
                <span className="size-6 animate-skeleton rounded-full bg-skeleton" />
                <span className="h-3 w-32 animate-skeleton rounded-full bg-skeleton" />
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="px-3 py-2.5 text-label text-fg-secondary">No matches</div>
        ) : (
          GROUP_ORDER.map((group) => {
            const groupItems = items.filter((item) => item.group === group);
            if (!groupItems.length) return null;
            return (
              <div key={group} role="group" aria-label={GROUP_TITLES[group]}>
                <div aria-hidden="true" className={menuGroupLabel}>
                  {GROUP_TITLES[group]}
                </div>
                {groupItems.map((item) => {
                  const i = items.indexOf(item);
                  const active = i === activeIndex;
                  return (
                    // Tastatur führt das Textfeld des Composers über aria-activedescendant.
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                    <div
                      key={item.id}
                      id={mentionOptionId(id, i)}
                      role="option"
                      aria-selected={active}
                      tabIndex={-1}
                      onMouseMove={() => onActiveIndexChange(i)}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => onSelect(item)}
                      className={cn(
                        menuItemBase,
                        'min-h-10 gap-2.5 px-2 py-1.5',
                        active && 'bg-tile',
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className="inline-flex size-6 shrink-0 items-center justify-center [&>*]:size-6"
                      >
                        {item.avatar}
                      </span>
                      <span className="max-w-[60%] shrink-0 truncate text-button text-fg">
                        {group === 'channel' ? '@' : ''}
                        {item.label}
                      </span>
                      {item.description ? (
                        <span className="min-w-0 flex-1 truncate text-label text-fg-secondary">
                          {item.description}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
